import { useMemo, useEffect, useState, useRef, useCallback } from "react";
import ax from "../../config/axios";

const DAYS_OF_WEEK = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
] as const;

// On mount, roll data is pre-fetched for ±PREFETCH_WEEKS around the current week.
// This allows instant client-side navigation without additional API calls.
// If the user navigates beyond this window, a supplementary fetch extends the cache.
const PREFETCH_WEEKS = 12;

// YYYY-MM-DD -> "Apr 15" style display string
const formatDateString = (dateString: string): string => {
    const date = new Date(dateString + "T00:00:00");
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
    });
};

const dateToYYYYMMDD = (date: Date): string => {
    return date.toISOString().slice(0, 10);
};

const getTodayYYYYMMDD = (): string => {
    return dateToYYYYMMDD(new Date());
};

// Returns Monday of the real-world current week (independent of weekOffset)
const getCurrentMonday = (): Date => {
    const today = new Date();
    const currentDay = today.getDay();
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);
    monday.setHours(0, 0, 0, 0);
    return monday;
};

// Shift a Monday forward or backward by a number of weeks
const shiftMonday = (baseMonday: Date, weeks: number): Date => {
    const shifted = new Date(baseMonday);
    shifted.setDate(baseMonday.getDate() + weeks * 7);
    return shifted;
};

// Produce an array of 7 YYYY-MM-DD strings (Mon-Sun) starting from a given Monday
const getWeekDates = (monday: Date): string[] => {
    return DAYS_OF_WEEK.map((_, index) => {
        const date = new Date(monday);
        date.setDate(monday.getDate() + index);
        return dateToYYYYMMDD(date);
    });
};

// "Apr 13 – Apr 19" style range label for a week
const formatWeekRange = (weekDates: string[]): string => {
    return `${formatDateString(weekDates[0])} \u2013 ${formatDateString(weekDates[6])}`;
};

interface RollItem {
    root: string;
    active_contract: string;
    first_notice_date: string; // YYYY-MM-DD
    rollStart: string; // YYYY-MM-DD
    rollEnd: string; // YYYY-MM-DD
}

export default function RollAgenda() {
    const [rollData, setRollData] = useState<RollItem[]>([]);

    // weekOffset controls which two weeks are displayed:
    //   0  = current week (left) + next week (right)  — the default view
    //  -1  = previous week (left) + current week (right)
    //  +1  = next week (left) + week after next (right)
    const [weekOffset, setWeekOffset] = useState(0);

    // Tracks the date range already fetched so the out-of-range guard
    // knows when a supplementary fetch is needed.
    const fetchedRange = useRef<{ start: string; end: string } | null>(null);

    // Stable reference to the real-world Monday — doesn't change with navigation
    const currentMonday = useMemo(() => getCurrentMonday(), []);
    const todayString = useMemo(() => getTodayYYYYMMDD(), []);

    // Recompute the two displayed weeks whenever weekOffset changes
    const { weekDateStrings, nextWeekDateStrings } = useMemo(() => {
        const displayMonday = shiftMonday(currentMonday, weekOffset);
        const nextMonday = shiftMonday(currentMonday, weekOffset + 1);
        return {
            weekDateStrings: getWeekDates(displayMonday),
            nextWeekDateStrings: getWeekDates(nextMonday),
        };
    }, [currentMonday, weekOffset]);

    // Dynamic card headers — show friendly "This Week" / "Next Week" labels
    // when the actual current or next calendar week is visible, otherwise
    // fall back to date ranges like "Apr 13 – Apr 19".
    const { leftHeader, rightHeader } = useMemo(() => {
        const leftRange = formatWeekRange(weekDateStrings);
        const rightRange = formatWeekRange(nextWeekDateStrings);

        let leftLabel: string;
        let rightLabel: string;

        if (weekOffset === 0) {
            leftLabel = `This Week (${leftRange})`;
            rightLabel = `Next Week (${rightRange})`;
        } else if (weekOffset === -1) {
            leftLabel = leftRange;
            rightLabel = `This Week (${rightRange})`;
        } else if (weekOffset === 1) {
            leftLabel = `Next Week (${leftRange})`;
            rightLabel = rightRange;
        } else {
            leftLabel = leftRange;
            rightLabel = rightRange;
        }

        return { leftHeader: leftLabel, rightHeader: rightLabel };
    }, [weekDateStrings, nextWeekDateStrings, weekOffset]);

    // Fetches roll data for a date range and merges it into the existing cache.
    // Deduplication uses a composite key of root + rollStart + rollEnd.
    // Also expands fetchedRange to track the total window we've loaded.
    const fetchRollData = useCallback((startDate: string, endDate: string) => {
        ax.get("data/roll-dates", {
            params: { startDate, endDate },
        }).then((response) => {
            setRollData((prev) => {
                const existingKeys = new Set(
                    prev.map((r) => `${r.root}|${r.rollStart}|${r.rollEnd}`)
                );
                const newItems = (response.data as RollItem[]).filter(
                    (r) => !existingKeys.has(`${r.root}|${r.rollStart}|${r.rollEnd}`)
                );
                return newItems.length > 0 ? [...prev, ...newItems] : prev;
            });

            fetchedRange.current = {
                start: fetchedRange.current
                    ? startDate < fetchedRange.current.start
                        ? startDate
                        : fetchedRange.current.start
                    : startDate,
                end: fetchedRange.current
                    ? endDate > fetchedRange.current.end
                        ? endDate
                        : fetchedRange.current.end
                    : endDate,
            };
        });
    }, []);

    // Initial wide fetch on mount — covers ±PREFETCH_WEEKS around the current
    // week so that clicking Prev/Next is instant with no API round-trip.
    useEffect(() => {
        const startMonday = shiftMonday(currentMonday, -PREFETCH_WEEKS);
        const endSunday = shiftMonday(currentMonday, PREFETCH_WEEKS + 1);
        endSunday.setDate(endSunday.getDate() - 1);
        fetchRollData(dateToYYYYMMDD(startMonday), dateToYYYYMMDD(endSunday));
    }, [currentMonday, fetchRollData]);

    // Out-of-range guard: if the user navigates beyond the pre-fetched window,
    // extend the cache by fetching another PREFETCH_WEEKS chunk in that direction.
    useEffect(() => {
        if (!fetchedRange.current) return;
        const displayStart = weekDateStrings[0];
        const displayEnd = nextWeekDateStrings[6];

        if (displayStart < fetchedRange.current.start) {
            const extendStart = shiftMonday(
                new Date(displayStart + "T00:00:00"),
                -PREFETCH_WEEKS
            );
            fetchRollData(dateToYYYYMMDD(extendStart), fetchedRange.current.start);
        }
        if (displayEnd > fetchedRange.current.end) {
            const extendEnd = shiftMonday(
                new Date(displayEnd + "T00:00:00"),
                PREFETCH_WEEKS
            );
            fetchRollData(fetchedRange.current.end, dateToYYYYMMDD(extendEnd));
        }
    }, [weekDateStrings, nextWeekDateStrings, fetchRollData]);

    const isToday = (dateString: string): boolean => dateString === todayString;

    const getRollsForDate = (dateString: string): RollItem[] => {
        return rollData.filter(
            (roll) => roll.rollStart === dateString || roll.rollEnd === dateString
        );
    };

    const isRollStart = (roll: RollItem, dateString: string): boolean =>
        roll.rollStart === dateString;

    const isRollEnd = (roll: RollItem, dateString: string): boolean =>
        roll.rollEnd === dateString;

    return (
        <div className="w-100 h-100 p-4 px-5 d-flex flex-column gap-3">
            {/* Title card with week navigation controls */}
            <div className="d-flex justify-content-center">
                <div className="card px-5 py-3 w-25">
                    <h1 className="fw-semibold text-center mb-3">Roll Agenda</h1>
                    <div className="d-flex justify-content-center align-items-center gap-4 ">
                        <button
                            className="btn btn-outline-primary"
                            onClick={() => setWeekOffset((o) => o - 1)}
                        >
                            &laquo; Prev
                        </button>
                        <button
                            className={`btn ${weekOffset === 0 ? "btn-outline-secondary" : "btn-primary"}`}
                            onClick={() => setWeekOffset(0)}
                            disabled={weekOffset === 0}
                        >
                            Today
                        </button>
                        <button
                            className="btn btn-outline-primary"
                            onClick={() => setWeekOffset((o) => o + 1)}
                        >
                            Next &raquo;
                        </button>
                    </div>
                </div>
            </div>

            {/* Two-week panel area — left panel is weekOffset, right panel is weekOffset+1 */}
            <div style={{ flex: "1 1 0", minHeight: 0 }}>
                <div className="d-flex flex-row h-100 justify-content-center gap-4 px-5">
                    {/* Left week panel */}
                    <div className="card h-100" style={{ width: "50%" }}>
                        <div className="card-header">
                            <h2 className="text-center fw-semibold">{leftHeader}</h2>
                        </div>
                        <div className="card-body pb-0 overflow-auto">
                            <div className="d-flex flex-column h-100" style={{ gap: "0.5rem" }}>
                                {DAYS_OF_WEEK.map((day, dayIndex) => {
                                    const dateString = weekDateStrings[dayIndex];
                                    const isCurrentDay = isToday(dateString);
                                    const rollsForDate = getRollsForDate(dateString);
                                    const isLastRow = dayIndex === DAYS_OF_WEEK.length - 1;

                                    return (
                                        <div
                                            key={dayIndex}
                                            className={`${isLastRow ? '' : 'border-bottom'} pb-2 d-flex flex-column`}
                                            style={{
                                                flex: "1 1 0",
                                                minHeight: 0,
                                                backgroundColor: isCurrentDay ? "rgba(13, 110, 253, 0.1)" : "transparent",
                                                borderRadius: isCurrentDay ? "0.375rem" : "0",
                                                padding: isCurrentDay ? "0.5rem" : "0",
                                            }}
                                        >
                                            <h5 className="fw-semibold mb-2">
                                                {day} - {formatDateString(dateString)}
                                                {isCurrentDay && (
                                                    <span className="badge bg-primary ms-2">Today</span>
                                                )}
                                            </h5>
                                            <div
                                                className="d-flex flex-wrap gap-2 overflow-auto align-items-start"
                                                style={{
                                                    minHeight: "50px",
                                                    padding: "0.5rem",
                                                }}
                                            >
                                                {/* Green badge = roll starts on this date, red badge = roll ends */}
                                                {rollsForDate.map((roll) => {
                                                    if (isRollStart(roll, dateString)) {
                                                        return (
                                                            <div
                                                                key={roll.root}
                                                                className="badge bg-success d-flex align-items-center"
                                                                style={{ maxHeight: "35px" }}
                                                            >
                                                                <span className="fs-6"><b>{roll.root}</b> - {roll.active_contract} - Ends on: {formatDateString(roll.rollEnd)}</span>
                                                            </div>
                                                        );
                                                    } else if (isRollEnd(roll, dateString)) {
                                                        return (
                                                            <div
                                                                key={roll.root}
                                                                className="badge bg-danger d-flex align-items-center"
                                                                style={{ maxHeight: "35px" }}
                                                            >
                                                                <span className="fs-6"><b>{roll.root}</b> - {roll.active_contract} - Started on: {formatDateString(roll.rollStart)}</span>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Right week panel */}
                    <div className="card h-100" style={{ width: "50%" }}>
                        <div className="card-header">
                            <h2 className="text-center fw-semibold">{rightHeader}</h2>
                        </div>
                        <div className="card-body">
                            <div className="d-flex flex-column h-100" style={{ gap: "0.5rem" }}>
                                {DAYS_OF_WEEK.map((day, dayIndex) => {
                                    const dateString = nextWeekDateStrings[dayIndex];
                                    const isCurrentDay = isToday(dateString);
                                    const rollsForDate = getRollsForDate(dateString);
                                    const isLastRow = dayIndex === DAYS_OF_WEEK.length - 1;

                                    return (
                                        <div
                                            key={dayIndex}
                                            className={`${isLastRow ? '' : 'border-bottom'} pb-2 d-flex flex-column`}
                                            style={{
                                                flex: "1 1 0",
                                                minHeight: 0,
                                                backgroundColor: isCurrentDay ? "rgba(13, 110, 253, 0.1)" : "transparent",
                                                borderRadius: isCurrentDay ? "0.375rem" : "0",
                                                padding: isCurrentDay ? "0.5rem" : "0",
                                            }}
                                        >
                                            <h5 className="fw-semibold mb-2">
                                                {day} - {formatDateString(dateString)}
                                                {isCurrentDay && (
                                                    <span className="badge bg-primary ms-2">Today</span>
                                                )}
                                            </h5>
                                            <div
                                                className="d-flex flex-wrap gap-2 overflow-auto align-items-start"
                                                style={{
                                                    minHeight: "50px",
                                                    padding: "0.5rem",
                                                }}
                                            >
                                                {rollsForDate.map((roll) => {
                                                    if (isRollStart(roll, dateString)) {
                                                        return (
                                                            <div
                                                                key={roll.root}
                                                                className="badge bg-success d-flex align-items-center"
                                                                style={{ maxHeight: "35px" }}
                                                            >
                                                                <span className="fs-6"><b>{roll.root}</b> - {roll.active_contract} - Ends on: {formatDateString(roll.rollEnd)}</span>
                                                            </div>
                                                        );
                                                    } else if (isRollEnd(roll, dateString)) {
                                                        return (
                                                            <div
                                                                key={roll.root}
                                                                className="badge bg-danger d-flex align-items-center p-1"
                                                                style={{ maxHeight: "35px" }}
                                                            >
                                                                <span className="fs-6"><b>{roll.root}</b> - {roll.active_contract} - Started on: {formatDateString(roll.rollStart)}</span>
                                                            </div>
                                                        );
                                                    }
                                                    return null;
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
